import Util from '../../js/util';

const instances = [];

export const EventName = {
  SHOW: 'onShow',
  SHOWN: 'onShown',
  HIDE: 'onHide',
  HIDDEN: 'onHidden',
  ON_REMOVE: 'onRemove',
  ON_UPDATE: 'onUpdate'
};

export const ClassName = {
  SHOW: 'show',
  COLLAPSE: 'collapse',
  COLLAPSING: 'collapsing',
  COLLAPSED: 'collapsed'
};

const Dimension = {
  WIDTH: 'width',
  HEIGHT: 'height'
};

export const Selector = {
  ACTIVES: '.show, .collapsing',
  DATA_MOUNT: '[data-mount="collapse"]'
};

function _getDimension() {
  const hasWidth = this.el.classList.contains(Dimension.WIDTH);
  return hasWidth ? Dimension.WIDTH : Dimension.HEIGHT;
}

function _addAriaAndCollapsedClass(element, triggerArray) {
  const isOpen = element.classList.contains(ClassName.SHOW);

  if (triggerArray.length) {
    triggerArray.forEach((triggerItem) => {
      triggerItem.classList.toggle(ClassName.COLLAPSED, !isOpen);
      triggerItem.setAttribute('aria-expanded', isOpen);
    });
  }
}

class Collapse {
  /**
   * Create a Collapse instance
   * @param {Object} opts - the Collapse options
   * @param {HTMLElement} opts.el - the Collapse trigger element
   * @param {boolean} [opts.toggle=false] - whether to toggle the Collapse on initialization
   * @param {HTMLElement} [opts.parent] - the parent (accordion) element for group management
   * @param {boolean} [opts.addEventListener=true] - whether to add event listeners on Collapse trigger *Possible carryover from Bootstrap
   */
  constructor({ el, toggle = false, parent, addEventListener = true }) {
    this.isTransitioning = false;
    this.isCollapsed = true;
    this.triggerElement = el;
    if (this.triggerElement.getAttribute('aria-expanded').toString() === 'true') {
      this.isCollapsed = false;
    }

    // Get the affected selectors
    const selector = Util.getSelectorFromElement(this.triggerElement);
    this.el = document.querySelector(selector);

    // The toggleArray is all of the buttons that control this Collapse's content
    this.toggleArray = Array.from(
      document.querySelectorAll(`[href="#${this.el.id}"],[data-target="#${this.el.id}"]`)
    );
    this.events = [];

    // Create custom events.
    this[EventName.SHOWN] = new CustomEvent(EventName.SHOWN);
    this[EventName.HIDDEN] = new CustomEvent(EventName.HIDDEN);

    // Find all auto-initialized Collapse buttons
    const toggleList = Array.from(document.querySelectorAll(Selector.DATA_MOUNT));
    toggleList.forEach((elem) => {
      // Find buttons with same the data-target as the triggerElement
      const selector = Util.getSelectorFromElement(elem);
      const filterElement = Array.from(document.querySelectorAll(selector)).filter(
        (foundElem) => foundElem === this.triggerElement
      );

      // If any buttons have the same data-target as the triggerElement, add them to the toggleArray
      if (selector !== null && filterElement.length) {
        this.toggleArray.push(elem);
      }
    });

    this.parent = this.el.getAttribute('data-parent');

    if (!parent) {
      _addAriaAndCollapsedClass.bind(this)(this.el, this.toggleArray);
    }

    if (toggle) {
      this.toggle();
    }

    // Add event handlers
    if (addEventListener) {
      this.events = [
        {
          el,
          type: 'click',
          handler: (event) => {
            // preventDefault only for <a> elements (which change the URL) not inside the collapsible element
            if (event.currentTarget.tagName === 'A') {
              event.preventDefault();
            }

            // If other collapses are transitioning, prevent interaction with this one
            if (this.#areSiblingsTransitioning()) {
              return;
            }

            this.toggle();
          }
        }
      ];

      Util.addEvents(this.events);
    }

    instances.push(this);
  }

  #getCollapses() {
    let collapses = [];

    if (this.parent) {
      collapses = Collapse.getInstances();
    }

    return collapses;
  }

  #areSiblingsTransitioning() {
    const collapses = this.#getCollapses();
    let isTransitioning = false;

    collapses.forEach((collapse) => {
      if (collapse !== this && collapse.parent === this.parent && collapse.isTransitioning) {
        isTransitioning = true;
      }
    });

    return isTransitioning;
  }

  /**
   * Toggles the collapse from show to hide and vice versa
   */
  toggle() {
    if (this.el.classList.contains(ClassName.SHOW)) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Shows the collapse
   */
  show() {
    if (this.isTransitioning || this.el.classList.contains(ClassName.SHOW)) {
      return;
    }

    // Create and dispatch custom event
    this[EventName.SHOW] = new CustomEvent(EventName.SHOW, { cancelable: true });
    this.el.dispatchEvent(this[EventName.SHOW]);
    if (this[EventName.SHOW].defaultPrevented) {
      return;
    }

    const dimension = _getDimension.bind(this)();

    this.el.classList.remove(ClassName.COLLAPSE);
    this.el.classList.add(ClassName.COLLAPSING);

    this.el.style[dimension] = 0;

    if (this.toggleArray.length) {
      this.toggleArray.forEach((elem) => {
        elem.classList.remove(ClassName.COLLAPSED);
        elem.setAttribute('aria-expanded', 'true');
      });
    }

    this.isTransitioning = true;

    // If we have a parent (group management), hide the other elements when other is shown
    if (this.parent) {
      const collapseInstances = this.#getCollapses();
      collapseInstances.forEach((collapse) => {
        if (collapse !== this && collapse.parent === this.parent && !collapse.isCollapsed) {
          // Hide the collapse
          collapse.toggle();
        }
      });
    }

    const complete = () => {
      this.el.classList.remove(ClassName.COLLAPSING);
      this.el.classList.add(ClassName.COLLAPSE);
      this.el.classList.add(ClassName.SHOW);

      this.el.style[dimension] = '';

      this.isTransitioning = false;
      this.isCollapsed = false;
      this.el.dispatchEvent(this[EventName.SHOWN]);
    };

    const capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1);
    const scrollSize = `scroll${capitalizedDimension}`;
    const transitionDuration = Util.getTransitionDurationFromElement(this.el);

    this.el.addEventListener(Util.TRANSITION_END, complete.bind(this), { once: true });
    Util.emulateTransitionEnd(this.el, transitionDuration);

    this.el.style[dimension] = `${this.el[scrollSize]}px`;
  }

  /**
   * Hides the collapse
   */
  hide() {
    if (this.isTransitioning || !this.el.classList.contains(ClassName.SHOW)) {
      return;
    }

    // Create and dispatch custom event
    this[EventName.HIDE] = new CustomEvent(EventName.HIDE, { cancelable: true });
    this.el.dispatchEvent(this[EventName.HIDE]);
    if (this[EventName.HIDE].defaultPrevented) {
      return;
    }

    const dimension = _getDimension.bind(this)();

    this.el.style[dimension] = `${this.el.getBoundingClientRect()[dimension]}px`;

    Util.reflow(this.el);

    this.el.classList.add(ClassName.COLLAPSING);
    this.el.classList.remove(ClassName.COLLAPSE);
    this.el.classList.remove(ClassName.SHOW);

    this.toggleArray.forEach((toggle) => {
      const toggleSelector = Util.getSelectorFromElement(toggle);

      if (toggleSelector !== null) {
        const toggleArray = Array.from(document.querySelectorAll(toggleSelector));
        toggleArray.forEach((el) => {
          if (!el.classList.contains(ClassName.SHOW)) {
            toggle.classList.add(ClassName.COLLAPSED);
            toggle.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });

    this.isTransitioning = true;

    const complete = () => {
      this.isTransitioning = false;
      this.el.classList.remove(ClassName.COLLAPSING);
      this.el.classList.add(ClassName.COLLAPSE);
      this.isCollapsed = true;
      this.el.dispatchEvent(this[EventName.HIDDEN]);
    };

    this.el.style[dimension] = '';
    const transitionDuration = Util.getTransitionDurationFromElement(this.el);

    this.el.addEventListener(Util.TRANSITION_END, complete.bind(this), { once: true });
    Util.emulateTransitionEnd(this.el, transitionDuration);
  }

  /**
   * Update instance
   */
  update() {
    // Create and dispatch custom event
    this[EventName.ON_UPDATE] = new CustomEvent(EventName.ON_UPDATE, { bubbles: true });
    this.el.dispatchEvent(this[EventName.ON_UPDATE]);
  }

  /**
   * Remove the event listener and the instance
   */
  remove() {
    Util.removeEvents(this.events);

    // remove this collapse reference from array of instances
    const index = instances.indexOf(this);
    instances.splice(index, 1);

    // Create and dispatch custom event
    this[EventName.ON_REMOVE] = new CustomEvent(EventName.ON_REMOVE, { bubbles: true });
    this.el.dispatchEvent(this[EventName.ON_REMOVE]);
  }

  /**
   * Get instances.
   * @returns {Object[]} An array of instances
   */
  static getInstances() {
    return instances;
  }
}

export default Collapse;
